<script lang="ts" setup>
import { computed, inject, nextTick, onMounted, provide, ref, watch } from 'vue'

import ChartTab from '@/components/result-drawer/ChartTab.vue'
import CalculationForm from '@/components/calculation/CalculationForm.vue'
import ChartLegend from '@/components/result-drawer/ChartLegend.vue'

import { cptChartDataFromCalculationCPT } from '@/utils/calculation-cpt/chartDataCalculationCPT'
import { cptChartDataFromCPT } from '@/utils/cpt/chartDataCPT'

import type { chartComponentType } from '@/types/chartComponent.type'
import type { CalculationType } from '@/stores/type/calculation.type'
import type { ChoiceType } from '@/stores/type/store.type'
import { DEFAULT_INPUT_COLOR, METHOD_LINE_COLOR } from '@/utils/lineColor'
import {
	BOULANGER_IDRISS_PROB,
	type LiquefactionMethodType,
	MOSS_PROB
} from '@/stores/type/calculationCPTDataPoint.type'
import type { chartInputInfoType, chartOutputInfoType } from '@/types/chartType'
import { storeToRefs } from 'pinia'

const cptStore = useCPTStore()
const calculationCPTStore = useCalculationCPTStore()
const calculationStore = useCalculationStore()
const calculationParameterStore = useCalculationParameterStore()

const { currentItem: currentCPT } = storeToRefs(cptStore)
const { currentItem: currentCalculationCPT } = storeToRefs(calculationCPTStore)
const { fields: calculationFields } = storeToRefs(calculationStore)
const { fields: calculationParameterFields } = storeToRefs(calculationParameterStore)

const appHeaderHeight = inject('appHeaderHeight') as Ref<number>

const ignoreHeight = computed(() => {
	return appHeaderHeight.value + 96 + 'px'
})

const chartLegend = ref<InstanceType<typeof ChartLegend> | null>(null)
const chartLegendHeight = ref(0)
provide('chartLegendHeight', chartLegendHeight)

const tab = ref<'calculation' | 'interpretation' | 'liquefaction'>('calculation')

watch(
	() => tab.value,
	async (val) => {
		if (val) {
			await nextTick()
			const height = chartLegend.value?.$el?.offsetHeight || 0
			chartLegendHeight.value = val === 'liquefaction' ? height : 0
		}
	}
)

const chartTab = ref<InstanceType<typeof ChartTab>>()

const currentCalculation = computed<CalculationType | null>(() => {
	return currentCalculationCPT.value
		? calculationStore.getCalculationById(currentCalculationCPT.value.calculation)
		: null
})

const liquefactionMethodChoices = computed<ChoiceType<LiquefactionMethodType>[]>(() => {
	return (calculationFields.value?.['liquefaction_method']?.child?.choices ??
		[]) as unknown as ChoiceType<LiquefactionMethodType>[]
})

const showLiqMethodLegend = ref<LiquefactionMethodType[]>([])
provide('showLiqMethodLegend', showLiqMethodLegend)

type legendDataType = { display_name: string; color: string; value: LiquefactionMethodType }

const legendData = computed(() => {
	const legend: legendDataType[] = []
	const liquefaction_method = currentCalculation.value?.liquefaction_method
	liquefactionMethodChoices.value.forEach((item) => {
		const color = METHOD_LINE_COLOR[item.value]
		if (color && liquefaction_method?.includes(item.value)) {
			legend.push({ display_name: item.display_name, color, value: item.value })
		}
	})
	return legend
})

const INPUT_CHART_FIELDS: chartInputInfoType[] = [
	{
		field: 'cone_resistance',
		xAxisTitle: 'q<sub>c</sub> [MPa]',
		title: 'Cone Resistance',
		color: DEFAULT_INPUT_COLOR,
		mode: 'marker'
	},
	{
		field: 'friction_ratio',
		xAxisTitle: 'r<sub>f</sub> [%]',
		title: 'Friction Ratio',
		color: DEFAULT_INPUT_COLOR,
		mode: 'marker'
	},
	{
		field: 'groundwater_pressure',
		xAxisTitle: 'u<sub>2</sub> [kPa]',
		title: 'Water Pressure',
		color: DEFAULT_INPUT_COLOR,
		mode: 'marker'
	}
]

// 1st row: CSR, CRR, factor of safety, settlement
// 2nd row: excess pore pressure- seismic, qc reduction seismic,  excess pore pressure- post seismic, qc reduction post seismic
// 3rd row: LPI, LDI, LSN, PL (if applied)
const OUTPUT_CHART_FIELDS: chartOutputInfoType[] = [
	{ field: 'CSR', xAxisTitle: 'CSR', title: 'Cycle Stress Ratio', mode: 'marker' },
	{ field: 'CRR', xAxisTitle: 'CRR', title: 'Cycle Resistance Ratio', mode: 'marker' },
	{
		field: 'fos_liq',
		xAxisTitle: 'FS [-]',
		title: 'Factor of Safety',
		mode: 'marker'
	},
	{ field: 's', xAxisTitle: 'Settlement [cm]', title: 'Settlement', mode: 'line' },
	{
		field: 'qc_s',
		xAxisTitle: 'qc reduction seismic [MPa]',
		title: 'qc reduction seismic',
		mode: 'marker'
	},
	{
		field: 'qc_ps',
		xAxisTitle: 'qc reduction post seismic [MPa]',
		title: 'qc reduction post seismic',
		mode: 'marker'
	},
	{
		field: 'rud_s',
		xAxisTitle: 'r<sub>u;seis</sub> [-]',
		title: 'Excess Pore <br> Pressure Seismic',
		mode: 'marker'
	},
	{
		field: 'rud_ps',
		xAxisTitle: 'r<sub>u;post-seis</sub> [-]',
		title: 'Excess Pore <br> Pressure Post Seismic',
		mode: 'marker'
	},
	{ field: 'LPI', xAxisTitle: 'LPI [-]', title: 'Liquefaction <br> Potential Index', mode: 'line' },
	{ field: 'LDI', xAxisTitle: 'LDI [-]', title: 'Lateral <br> Displacement Index', mode: 'line' },
	{ field: 'LSN', xAxisTitle: 'LSN [-]', title: 'Liquefaction <br> Severity Number', mode: 'line' },
	{ field: 'PL', xAxisTitle: 'PL [%]', title: 'Probability <br> of liquefaction', mode: 'marker' }
]
const chartOutputInfo = computed<chartOutputInfoType[]>(() => {
	if (
		currentCalculation.value?.liquefaction_method.includes(BOULANGER_IDRISS_PROB) ||
		currentCalculation.value?.liquefaction_method.includes(MOSS_PROB)
	) {
		return OUTPUT_CHART_FIELDS
	} else {
		return OUTPUT_CHART_FIELDS.filter((item) => item.field !== 'PL')
	}
})

//Interpretation results
const inputChart = computed<chartComponentType[]>(() => {
	const data: chartComponentType[] = []
	INPUT_CHART_FIELDS.forEach((item) => {
		const chartData = cptChartDataFromCPT(currentCPT.value, item.field, item.color, item.mode)
		if (chartData.length) {
			data.push({
				data: chartData,
				xAxisTitle: item.xAxisTitle,
				title: item.title
			})
		}
	})
	return data
})

//Liquefaction results
const outputChart = computed<chartComponentType[]>(() => {
	const data: chartComponentType[] = []
	chartOutputInfo.value.forEach((item) => {
		const chartData = cptChartDataFromCalculationCPT(
			currentCalculationCPT.value,
			item.field,
			liquefactionMethodChoices.value,
			showLiqMethodLegend.value,
			item.mode
		)
		if (chartData.length) {
			data.push({
				data: chartData,
				xAxisTitle: item.xAxisTitle,
				title: item.title
			})
		}
	})
	return data
})

watch(
	() => currentCalculation.value,
	() => {
		showLiqMethodLegend.value = currentCalculation.value?.liquefaction_method || []
	}
)

watch(
	() => currentCalculationCPT.value,
	() => {
		chartTab.value?.chartRerender()
	}
)

onMounted(() => {
	showLiqMethodLegend.value = currentCalculation.value?.liquefaction_method || []
})
</script>

<template>
	<div class="fill-height">
		<v-tabs v-model="tab" bg-color="transparent" color="primary" grow>
			<v-tab rounded="0" class="text-none" value="calculation">Calculation input</v-tab>
			<v-tab rounded="0" class="text-none" value="interpretation">Interpretation results</v-tab>
			<v-tab rounded="0" class="text-none" value="liquefaction">Liquefaction results</v-tab>
		</v-tabs>
		<v-divider></v-divider>
		<v-window class="fill-height" v-model="tab">
			<v-window-item class="fill-tab" value="calculation">
				<v-list class="overflow-auto fill-height" v-if="currentCalculation">
					<CalculationForm
						v-if="currentCalculation && calculationParameterFields"
						:fields="calculationParameterFields"
						v-model:item="currentCalculation"
						readonly
					></CalculationForm>
				</v-list>
			</v-window-item>

			<v-window-item class="fill-tab" value="interpretation">
				<ChartTab class="overflow-auto" :data="inputChart" />
			</v-window-item>
			<v-window-item class="fill-tab" value="liquefaction">
				<ChartLegend ref="chartLegend" :data="legendData" />
				<v-divider />
				<ChartTab
					ref="chartTab"
					class="overflow-auto"
					:has-external-legend="true"
					:data="outputChart"
				/>
			</v-window-item>
		</v-window>
	</div>
</template>

<style scoped>
.fill-tab {
	height: calc(100vh - v-bind(ignoreHeight));
}
</style>
